import { Delete01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Alert02SolidRounded } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "react-router-dom";
import { SectionCard } from "shared/app/LeadsTableView/components/CreateLeadModal/SectionCard";
import { useFormController } from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize
} from "shared/components";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { cx } from "shared/utils";
import { formatPhone } from "shared/utils/format-phone";
import type { DuplicateContact } from "api/contacts";

const formatDuplicateValue = (dup: DuplicateContact) => {
  if (dup.type === "phone") return formatPhone(dup.normalized_value);
  return dup.value;
};

export const ContactInfoSection = ({
  form,
  wrapperClassName,
  isTitleDisabled = false,
  cardClassName,
  duplicates = [],
  onContactFieldBlur
}: {
  form: ReturnType<typeof useFormController>;
  wrapperClassName?: string;
  isTitleDisabled?: boolean;
  cardClassName?: string;
  duplicates?: DuplicateContact[];
  onContactFieldBlur?: () => void;
}) => {
  const prevPhoneFieldsNotReady =
    Boolean(form.errors.primary_phone) || Boolean(form.errors.other_phones);

  const prevEmailFieldsNotReady =
    Boolean(form.errors.primary_email) || Boolean(form.errors.other_emails);

  return (
    <SectionCard
      title="Contact information"
      wrapperClassName={cx("!p-0", wrapperClassName)}
      isTitleDisabled={isTitleDisabled}
      cardClassName={cardClassName}
    >
      <div className="px-8 pt-4 text-sm leading-5 text-content-700">
        Phone or email is required
      </div>
      {duplicates.length > 0 && (
        <div className="flex flex-col gap-2 px-8 pt-4">
          {duplicates.map((dup) => {
            const contactName = [dup.contact.first_name, dup.contact.last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <div
                key={`${dup.type}-${dup.normalized_value}-${dup.contact.id}`}
                className="flex items-center gap-2 text-base leading-6 font-medium text-warning-700"
              >
                <HugeiconsIcon
                  icon={Alert02SolidRounded}
                  size={20}
                  className="shrink-0 text-warning-600"
                />
                <span>
                  {formatDuplicateValue(dup)} is already in use by{" "}
                  <Link
                    to={`/contacts/${dup.contact.id}`}
                    className="font-bold underline"
                    target="_blank"
                  >
                    {contactName || "Unknown"}
                  </Link>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex w-full">
        {/* Phone Numbers Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8 border-r border-alpha-40">
          <div className="font-medium text-sm leading-5 text-content-700">
            Phone numbers
          </div>
          <div className="flex flex-col gap-4 ">
            <div>
              <BaseInput
                id="primaryPhone"
                value={form.values.primary_phone ?? ""}
                onChange={form.updatePrimaryPhone}
                onBlur={onContactFieldBlur}
                size={InputSize.BASE}
                error={form.errors.primary_phone}
                placeholder="(123) 456-7890"
                parser={tenDigitTelParser({ asString: true })}
              />
            </div>

            {form.values.other_phones?.map((phoneNumber, index) => {
              return (
                <div key={`phone-${index}`} className="flex gap-2">
                  <div className="flex-1">
                    <BaseInput
                      id={`phone-${index}`}
                      value={phoneNumber}
                      onChange={(value: string) =>
                        form.updateOtherPhone(index, value)
                      }
                      onBlur={onContactFieldBlur}
                      error={form.errors.other_phones?.[index]}
                      size={InputSize.BASE}
                      placeholder="(123) 456-7890"
                      parser={tenDigitTelParser({ asString: true })}
                    />
                  </div>
                  <IconButton
                    variant={IconButtonVariant.BASE}
                    size={IconButtonSize.BASE}
                    HugeIcon={Delete01Icon}
                    onClick={() => form.removeOtherPhone(index)}
                  />
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              tooltipZIndex={50}
              tooltipDelay={400}
              tooltip={
                prevPhoneFieldsNotReady
                  ? "Please fix errors in phone fields before adding a new one"
                  : undefined
              }
              disabled={prevPhoneFieldsNotReady}
              size={ButtonSize.BASE}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={async () => {
                const errors = await form.triggerPartialValidation([
                  "primary_phone",
                  "other_phones"
                ]);

                if (Object.keys(errors).length) return;

                form.addOtherPhone();
              }}
            >
              Add phone number
            </Button>
          </div>
        </div>

        {/* Emails Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8">
          <div className="font-medium text-sm leading-5 text-content-700">
            Emails
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <BaseInput
                id="primaryEmail"
                value={form.values.primary_email ?? ""}
                onChange={form.updatePrimaryEmail}
                onBlur={onContactFieldBlur}
                error={form.errors.primary_email}
                size={InputSize.BASE}
                placeholder="john.doe@acme.com"
              />
            </div>

            {form.values.other_emails?.map((otherEmail, localIndex) => {
              return (
                <div key={`email-${localIndex}`} className="flex gap-2">
                  <div className="flex-1">
                    <BaseInput
                      id={`email-${localIndex}`}
                      value={otherEmail}
                      onChange={(value: string) =>
                        form.updateOtherEmail(localIndex, value)
                      }
                      onBlur={onContactFieldBlur}
                      error={form.errors.other_emails?.[localIndex]}
                      size={InputSize.BASE}
                      placeholder="john.doe@acme.com"
                    />
                  </div>
                  <IconButton
                    variant={IconButtonVariant.BASE}
                    size={IconButtonSize.BASE}
                    HugeIcon={Delete01Icon}
                    onClick={() => form.removeOtherEmail(localIndex)}
                  />
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              tooltipZIndex={50}
              tooltipDelay={300}
              tooltip={
                prevEmailFieldsNotReady
                  ? "Please fix errors in email fields before adding a new one"
                  : undefined
              }
              disabled={prevEmailFieldsNotReady}
              size={ButtonSize.BASE}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={async () => {
                const errors = await form.triggerPartialValidation([
                  "primary_email",
                  "other_emails"
                ]);

                if (Object.keys(errors).length) return;

                form.addOtherEmail();
              }}
            >
              Add email
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
